// Volver a firmar, justo antes de usarlas, las URLs firmadas de Storage que están por
// vencer — ADR-2026-09-12-las-fotos-viajan-con-firma-fresca.
//
// El chat le manda al modelo fotos por URL (las de producto con `ver_productos`, los
// fondos y poses elegidos, las generadas recientes, los frames) y a la generación como
// `reference_images`. Casi todas son URLs firmadas de una hora tomadas al cargar el
// catálogo, y no se renovaban nunca: con la pestaña abierta varias horas, Anthropic
// intentaba bajarlas ya vencidas y rechazaba el turno entero ("Unable to download the
// file"), que el chat mostraba como "No pude conectarme".
//
// Acá se renuevan en lote, por bucket, sólo las que están por vencer. Lo que no es una
// URL firmada de Storage se deja como está.

import { supabase } from '../lib/supabase';
import {
  agruparAligerables,
  agruparPorVencer,
  reemplazarUrlsEnMensajes,
  transformadasPorVencer,
  urlsEnMensajes,
} from './firmaDeStorage';
import { firmarEnLote } from './firmarEnLote';

/** Con cuánto se renueva: una hora, como el resto del catálogo. */
export const TTL_FIRMA_FRESCA_S = 3600;

/**
 * Devuelve un mapa `URL vieja → URL nueva` con las que hizo falta renovar. Las que no se
 * pudieron firmar (el objeto ya no existe) no están en el mapa: quedan como estaban.
 */
export async function refrescarFirmas(urls: string[]): Promise<Map<string, string>> {
  const renovadas = new Map<string, string>();
  const porBucket = agruparPorVencer(urls);
  for (const [bucket, paths] of porBucket) {
    const lista = Array.from(paths.keys());
    try {
      const { data } = await supabase.storage.from(bucket).createSignedUrls(lista, TTL_FIRMA_FRESCA_S);
      (data ?? []).forEach((item, i) => {
        const path = item.path ?? lista[i];
        const vieja = path ? paths.get(path) : undefined;
        if (vieja && item.signedUrl) renovadas.set(vieja, item.signedUrl);
      });
    } catch (err) {
      console.warn('[firmasFrescas] no se pudo renovar', bucket, err);
    }
  }
  // Las reescaladas por Storage (`render/image`, hoy las miniaturas para identificar
  // creaciones) se firman de a una: el lote no acepta transformación, y firmarlas sin
  // ella devolvería el original de 800 px que se quiso evitar.
  for (const t of transformadasPorVencer(urls)) {
    try {
      const { data } = await supabase.storage
        .from(t.bucket)
        .createSignedUrl(t.path, TTL_FIRMA_FRESCA_S, { transform: t.transform });
      if (data?.signedUrl) renovadas.set(t.url, data.signedUrl);
    } catch (err) {
      console.warn('[firmasFrescas] no se pudo renovar la miniatura', t.bucket, t.path, err);
    }
  }
  return renovadas;
}

/** Las mismas URLs, en el mismo orden, con las firmas por vencer ya renovadas. */
export async function conFirmasFrescas(urls: string[]): Promise<string[]> {
  if (urls.length === 0) return urls;
  const renovadas = await refrescarFirmas(urls);
  return urls.map((u) => renovadas.get(u) ?? u);
}

/**
 * Las mismas referencias, en el mismo orden, con la `url` por vencer ya renovada. Es la
 * red de la GENERACIÓN: las tarjetas del chat, el lote y Prompt libre arman sus
 * referencias con fotos que pueden venir firmadas de hace rato (la lista de modelos y
 * de poses propias se firma al cargar; las adjuntas, al enviar el mensaje), y entre eso y
 * tocar Generar puede pasar más de una hora —el worker se comía un 400 bajándolas y la
 * tanda entera fallaba (ADR-2026-10-06-la-foto-del-modelo-se-refirma-en-el-chat). Lo que
 * no es una URL firmada nuestra (públicas, sin url) queda como está.
 */
export async function conReferenciasFrescas<T extends { url?: string }>(refs: readonly T[]): Promise<T[]> {
  const urls = refs.flatMap((r) => (r.url ? [r.url] : []));
  if (urls.length === 0) return [...refs];
  const renovadas = await refrescarFirmas(urls);
  if (renovadas.size === 0) return [...refs];
  return refs.map((r) => (r.url && renovadas.has(r.url) ? { ...r, url: renovadas.get(r.url) as string } : r));
}

/** Renueva EN EL LUGAR las firmas por vencer de todos los bloques `image`/`document`
 *  por URL de los mensajes (también los de adentro de un `tool_result`). */
export async function refrescarFirmasEnMensajes(
  mensajes: Array<{ content: unknown }>,
): Promise<number> {
  const urls = urlsEnMensajes(mensajes);
  if (urls.length === 0) return 0;
  const renovadas = await refrescarFirmas(urls);
  if (renovadas.size === 0) return 0;
  return reemplazarUrlsEnMensajes(mensajes, renovadas);
}

/**
 * Cambia EN EL LUGAR, en todos los bloques `image` de los mensajes, el `full/` por su
 * `opt/` cuando existe (ADR-2026-09-14-el-modelo-ve-el-opt-y-el-error-habla-en-castellano).
 * Es lo que el modelo va a MIRAR: un thumbnail de 800px alcanza para reconocer, elegir y
 * describir, y pesa ~100 veces menos que el original que Anthropic no llegaba a bajar.
 *
 * Se firma en lote por bucket; el `opt/` que no existe (una creación vieja sin thumbnail)
 * no viene en el mapa y ese bloque queda con su `full/` — que después renueva
 * `refrescarFirmasEnMensajes` si hace falta. Devuelve cuántos bloques cambió.
 */
export async function aligerarImagenesEnMensajes(
  mensajes: Array<{ content: unknown }>,
): Promise<number> {
  const porBucket = agruparAligerables(mensajes);
  if (porBucket.size === 0) return 0;
  const reemplazos = new Map<string, string>();
  for (const [bucket, livianoAOriginal] of porBucket) {
    try {
      const firmadas = await firmarEnLote(Array.from(livianoAOriginal.keys()), bucket);
      for (const [liviano, original] of livianoAOriginal) {
        const url = firmadas.get(liviano);
        if (url) reemplazos.set(original, url);
      }
    } catch (err) {
      console.warn('[firmasFrescas] no se pudo firmar los opt/ de', bucket, err);
    }
  }
  if (reemplazos.size === 0) return 0;
  return reemplazarUrlsEnMensajes(mensajes, reemplazos);
}
